import { useState } from 'react';
import { Box, Button, Chip, Stack, TextField, Typography } from '@mui/material';
import { IconFolder, IconPlus } from '@tabler/icons-react';
import { calculateEconomics, rubles } from 'src/utils/intentEconomics';

import type { Workspace } from 'src/services/IntentService';

export default function ProjectOverview({
    projects,
    available,
    open,
    create,
}: {
    projects: Workspace[];
    available: boolean;
    open: (view: string, id: number) => void;
    create: () => void;
}) {
    const [query, setQuery] = useState('');
    const filtered = projects.filter((project) =>
        `${project.name} ${project.profile.description || ''}`
            .toLocaleLowerCase('ru-RU')
            .includes(query.trim().toLocaleLowerCase('ru-RU'))
    );
    return (
        <Box className="intent-project-overview">
            <TextField
                label="Найти поток"
                size="small"
                fullWidth
                value={query}
                onChange={(event) => setQuery(event.target.value)}
                type="search"
            />
            {!projects.length ? (
                <Box className="intent-overview-empty">
                    <IconFolder size={32} />
                    <Typography
                        component="h2"
                        variant="h6"
                    >
                        Отдельный поток для каждого бизнеса
                    </Typography>
                    <Typography color="text.secondary">
                        Описание, диалоги, потоки и воронка будут собраны в одном месте.
                    </Typography>
                    <Button
                        variant="contained"
                        startIcon={<IconPlus size={18} />}
                        disabled={!available}
                        onClick={create}
                    >
                        Создать первый поток
                    </Button>
                </Box>
            ) : !filtered.length ? (
                <Box className="intent-overview-empty">
                    <Typography>Нет потоков по этому запросу</Typography>
                    <Button onClick={() => setQuery('')}>Сбросить поиск</Button>
                </Box>
            ) : (
                <div className="intent-project-grid">
                    {filtered.map((project) => {
                        const economics = calculateEconomics(project.economics);
                        const running = project.streams.some((stream) => stream.is_active);
                        return (
                            <article
                                className="intent-project-card"
                                key={project.id}
                            >
                                <Stack
                                    direction="row"
                                    justifyContent="space-between"
                                    alignItems="center"
                                    gap={1}
                                >
                                    <IconFolder size={22} />
                                    <Chip
                                        size="small"
                                        variant="outlined"
                                        label={
                                            running
                                                ? 'Поиск работает'
                                                : project.streams.length
                                                ? 'Поиск остановлен'
                                                : 'Подготовка'
                                        }
                                    />
                                </Stack>
                                <Typography
                                    component="h2"
                                    variant="h6"
                                >
                                    {project.name}
                                </Typography>
                                <Typography
                                    className="intent-project-description"
                                    variant="body2"
                                    color="text.secondary"
                                >
                                    {project.profile.description || 'Добавьте описание бизнеса, чтобы настроить поиск.'}
                                </Typography>
                                <dl>
                                    <div>
                                        <dt>Осталось лидов</dt>
                                        <dd>
                                            {project.streams.reduce((sum, stream) => sum + Number(stream.balance), 0)}
                                        </dd>
                                    </div>
                                    <div>
                                        <dt>Ценность лида до привлечения</dt>
                                        <dd>{economics ? rubles(economics.value) : 'Не рассчитана'}</dd>
                                    </div>
                                </dl>
                                <Stack
                                    direction="row"
                                    gap={1}
                                    flexWrap="wrap"
                                >
                                    <Button
                                        variant="contained"
                                        onClick={() => open(running ? 'leads' : 'chat', project.id)}
                                    >
                                        Открыть поток
                                    </Button>
                                    <Button onClick={() => open('launch', project.id)}>Экономика</Button>
                                </Stack>
                            </article>
                        );
                    })}
                </div>
            )}
        </Box>
    );
}
